import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGroupMasterEntry } from './hooks/useGroupMasterEntry';
import { WEEKLY_OFF_OPTIONS, SECOND_WEEKLY_OFF_OPTIONS } from './api/groupMasterApi';
import AppDropdown from '../../../components/AppDropdown';

export default function EmployeeGroupEntryScreen() {
  const { state, actions } = useGroupMasterEntry();
  const navigate = useNavigate();
  const { form, isLoading, isSaving, isEdit } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Group Details...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Employee Group' : 'Create Employee Group'}
          </h1>
        </div>

        <Link
          to={state.listPath || "/payroll/employee-group"}
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Group Details</h2>
          </div>

          {/* Form Body matching Screenshot 3 */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-4">
              {/* Name */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => actions.patchForm('name', e.target.value)}
                  placeholder="Name"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Weekly Off */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Weekly Off <span className="text-error">*</span>
                </label>
                <AppDropdown
                  options={WEEKLY_OFF_OPTIONS}
                  value={form.weeklyOff}
                  onChange={(val) => actions.patchForm('weeklyOff', val)}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Second Weekly Off */}
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Second Weekly Off
                </label>
                <AppDropdown
                  options={SECOND_WEEKLY_OFF_OPTIONS}
                  value={form.secondWeeklyOff}
                  onChange={(val) => actions.patchForm('secondWeeklyOff', val)}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => actions.handleSave({ exitAfter: true })}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">save</span>
              <span>{isSaving ? 'Saving...' : 'Save & Exit'}</span>
            </button>

            {!isEdit && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => actions.handleSave({ exitAfter: false })}
                className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60"
              >
                Save &amp; Continue
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/payroll/employee-group')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right TIPS Panel */}
        <div className="lg:col-span-4 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
            <span className="material-symbols-outlined text-base text-amber-500">lightbulb</span>
            <span>TIPS:</span>
          </div>

          <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
            Employee Groups define the weekly off pattern for a set of employees, used in daily attendance processing and shift scheduling.
          </p>

          <div className="pt-3 border-t border-outline-variant/15 space-y-2">
            <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
              CONFIGURATION GUIDE:
            </h4>
            <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
              <li>
                <strong className="text-on-surface">Weekly Off:</strong> The primary weekly holiday (e.g., Sunday).
              </li>
              <li>
                <strong className="text-on-surface">Second Weekly Off:</strong> An optional additional weekly off day (e.g., 1st &amp; 3rd Saturday).
              </li>
              <li>
                <strong className="text-on-surface">Usage:</strong> Groups are assigned to employees in their master profile and applied during attendance processing.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
